import { observer } from "mobx-react-lite";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Button, Card, Layout } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import WorkbenchNewSearch from "../components/WorkbenchNewSearch";
import { useWorkbenchStore } from "../../workbench/stores/context";
import {
    WbSubject,
    WbSubjectOverallStatus,
} from "../../workbench/types/WbTypes";
import WorkbenchNewEntityTypeTag from "../components/WorkbenchNewEntityTypeTag";
import {
    formatViewedAtLabel,
    LastVisitedSubject,
    readLastVisitedSubject,
    saveLastVisitedSubject,
    toStoredSubject,
} from "../utils/lastVisitedSubject";

const workbenchNewQuickSearches = [
    "UND-52",
    "QBE Insurance (Europe) Limited",
    "Non-Proportional - Excess of loss",
    "SUB-2026-839214",
];

const subjectStatusLabelByValue: Record<WbSubjectOverallStatus, string> = {
    New: "New",
    InProgress: "In Progress",
    Completed: "Completed",
    OnHold: "On Hold",
};

const formatDateYear = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const year = new Date(value).getFullYear();

    return Number.isNaN(year) ? "" : String(year);
};

const getSubjectParty = (subject: WbSubject) =>
    subject.search?.party ??
    subject.cedent ??
    subject.context?.cedent ??
    subject.context?.claimant ??
    subject.context?.broker ??
    "";

const getSubjectPeriod = (subject: WbSubject) => {
    if (subject.search?.period) {
        return subject.search.period;
    }

    if (subject.kind === "Claim" && subject.context?.dateOfLoss) {
        return `Loss date ${subject.context.dateOfLoss}`;
    }

    if (subject.context?.uwYear) {
        return `UW Year ${subject.context.uwYear}`;
    }

    const periodYears = [
        formatDateYear(subject.periodFrom),
        formatDateYear(subject.periodTo),
    ].filter(Boolean);

    return Array.from(new Set(periodYears)).join("-");
};

const getSubjectTitle = (subject: WbSubject) => {
    if (subject.search?.title) {
        return subject.search.title;
    }

    const party = getSubjectParty(subject);
    const mainClass = subject.context?.mainClass?.trim();
    const year = subject.context?.uwYear;
    const base = party || subject.identifier;

    if (mainClass && year) {
        return `${base} ${mainClass} ${year}`;
    }

    if (mainClass) {
        return `${base} ${mainClass}`;
    }

    return base;
};

const WorkbenchNewPage: React.FC = () => {
    const { workbenchStore } = useWorkbenchStore();
    const navigate = useNavigate();
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [lastVisited, setLastVisited] = useState<LastVisitedSubject | null>(
        null,
    );

    useLayoutEffect(() => {
        workbenchStore.setSearchInput("");
    }, [workbenchStore]);

    useEffect(() => {
        setLastVisited(readLastVisitedSubject());
        void workbenchStore.loadInitialData();
    }, [workbenchStore]);

    const openSubject = (subject: WbSubject) => {
        saveLastVisitedSubject(subject);
        setLastVisited(readLastVisitedSubject());

        navigate(`/workbench-new/${encodeURIComponent(subject.id)}`, {
            state: { subject: toStoredSubject(subject) },
        });
    };

    const recentSubject = lastVisited?.subject ?? null;
    const recentSubjectTitle = recentSubject
        ? getSubjectTitle(recentSubject)
        : "";
    const recentSubjectParty = recentSubject
        ? getSubjectParty(recentSubject)
        : "";
    const recentSubjectPeriod = recentSubject
        ? getSubjectPeriod(recentSubject)
        : "";
    const recentViewedLabel = formatViewedAtLabel(lastVisited?.viewedAt);
    const recentStatusLabel = recentSubject
        ? subjectStatusLabelByValue[recentSubject.state.overallStatus]
        : "";

    return (
        <Layout className="screen-size workbench-new-layout">
            <Layout.Content className="workbench-new-main">
                <div className="workbench-new-content">
                    <section
                        className="workbench-new-entry"
                        aria-labelledby="workbench-new-title"
                    >
                        <div className="workbench-new-hero">
                            <h1 id="workbench-new-title">Search Workbench</h1>
                            <p>
                                Find a contract, claim or business case using
                                any detail you know.
                            </p>

                            <div className="workbench-new-search-stack">
                                <div className="workbench-new-search-container">
                                    <WorkbenchNewSearch
                                        error={
                                            workbenchStore.subjectSearchError
                                        }
                                        isLoading={
                                            workbenchStore.isSubjectSearchLoading
                                        }
                                        onSearchModeChange={setIsSearchMode}
                                        onSearch={
                                            workbenchStore.searchSubjectsNow
                                        }
                                        onSelect={openSubject}
                                        query={workbenchStore.searchInput}
                                        setQuery={workbenchStore.setSearchInput}
                                        suggestions={
                                            workbenchStore.subjectSuggestions
                                        }
                                        totalCount={
                                            workbenchStore.subjectSuggestionsTotalCount
                                        }
                                    />
                                </div>

                                {!isSearchMode && (
                                    <>
                                        <div className="workbench-new-examples-container">
                                            <div
                                                className="workbench-new-examples"
                                                aria-label="Example searches"
                                            >
                                                <span>Try:</span>
                                                {workbenchNewQuickSearches.map(
                                                    (example) => (
                                                        <Button
                                                            key={example}
                                                            onClick={() =>
                                                                workbenchStore.setSearchInput(
                                                                    example,
                                                                )
                                                            }
                                                        >
                                                            {example}
                                                        </Button>
                                                    ),
                                                )}
                                            </div>
                                        </div>

                                        {recentSubject && (
                                            <section
                                                aria-labelledby="workbench-new-recent-title"
                                                className="workbench-new-recent"
                                            >
                                                <div className="workbench-new-section-heading">
                                                    <h2 id="workbench-new-recent-title">
                                                        Continue where you left
                                                        off
                                                    </h2>
                                                    <Button type="link">
                                                        View recent history
                                                        <RightOutlined />
                                                    </Button>
                                                </div>
                                                <Card
                                                    className="workbench-new-recent-card"
                                                    hoverable={true}
                                                    onClick={() =>
                                                        openSubject(
                                                            recentSubject,
                                                        )
                                                    }
                                                >
                                                    <div className="workbench-new-recent-icon">
                                                        <span
                                                            aria-hidden="true"
                                                            className="workbench-new-recent-icon-glyph"
                                                        />
                                                    </div>
                                                    <div className="workbench-new-recent-main">
                                                        <WorkbenchNewEntityTypeTag
                                                            kind={
                                                                recentSubject.kind
                                                            }
                                                        />
                                                        <strong>
                                                            {recentSubjectTitle}
                                                        </strong>
                                                        <small>
                                                            {recentSubjectParty}
                                                            {recentSubjectParty &&
                                                                recentSubjectPeriod && (
                                                                    <span className="workbench-new-dot">
                                                                        .
                                                                    </span>
                                                                )}
                                                            {
                                                                recentSubjectPeriod
                                                            }
                                                            {(recentSubjectParty ||
                                                                recentSubjectPeriod) && (
                                                                <span className="workbench-new-dot">
                                                                    .
                                                                </span>
                                                            )}
                                                            {recentViewedLabel}
                                                        </small>
                                                    </div>
                                                    <div className="workbench-new-stage">
                                                        {recentStatusLabel}
                                                    </div>
                                                    <RightOutlined className="workbench-new-recent-chevron" />
                                                </Card>
                                            </section>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </Layout.Content>
        </Layout>
    );
};

export default observer(WorkbenchNewPage);
